import Route from '@ember/routing/route'
import RSVP from 'rsvp'
import { service } from '@ember/service'
import type Store from '@ember-data/store'
import type SessionService from 'teacher-ui-client/services/session'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Teacher from 'teacher-ui-client/models/teacher'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type { NormalizedInterval } from 'date-fns'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import { hasId } from 'teacher-ui-client/utils/type-guards'

export default class ReportingMathsSummaryTableRoute extends Route {
  @service studentEvents!: StudentEventsService

  @service session!: SessionService

  @service store!: Store

  subscriptionType = SubscriptionType.Maths

  get isSubco() {
    return this.session.currentTeacher.subcoFor(this.subscriptionType)
  }

  // used by teachers tab, has no scope selector, ignores scoped data
  get mathsTeachers() {
    return this.store
      .peekAll<Teacher>('teacher')
      .filter((teacher) => teacher.hasSchoolClassForSubscriptionType(this.subscriptionType))
  }

  async model() {
    const { scopedData, period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

    const scopedStudents = scopedData.students
    const scopes: { school: SchoolScope; students: StudentsScope } = {
      school: { scope: 'school', ids: [this.session.currentSchool.id] },
      students: { scope: 'students', ids: scopedStudents.map((s) => s.id) },
    }

    const product: LegacyProductMs = 'maths'
    const stats = await this.makeStatsRequests(product, scopes, period)

    return { product, students: scopedStudents, mathsTeachers: this.mathsTeachers, isSubco: this.isSubco, ...stats }
  }

  /**
   * Simple Summary endpoint is requested twice for subco users as they
   * need to display both students and teachers summaries.
   */
  makeStatsRequests(
    product: LegacyProductMs,
    scopes: { school: SchoolScope; students: StudentsScope },
    period: NormalizedInterval,
  ) {
    const requestData = this.getTeachersDataForSimpleSummary(this.mathsTeachers)

    return RSVP.hash({
      schoolSimpleSummary: this.isSubco
        ? this.studentEvents.simpleSummarySchool(product, scopes.school, period, requestData)
        : null,
      studentsSimpleSummary: this.studentEvents.simpleSummaryStudents(product, scopes.students, period),
    })
  }

  /**
   * Prepares data for the simple summary student events method
   * with teachers and their students
   */
  getTeachersDataForSimpleSummary(teachers: Teacher[]) {
    return {
      teachers: teachers.filter(hasId).map((teacher) => ({
        teacher_id: teacher.id,
        student_ids: teacher
          .studentsForSubscriptionType(this.subscriptionType)
          .filter(hasId)
          .map((student) => student.id),
      })),
    }
  }
}
